import type { AllFeatures, WorkbookUiFeatureName } from 'cadenza/features';
import { getDependentFeatures, isSingleFeatureAvailable, isWorkbookFeature } from 'cadenza/features';
import type { WorkbookState } from 'cadenza/workbook/workbook.state';
import {
  isWorkbookDataViewContentEditable,
  isWorkbookDesignerEnabled,
  isWorkbookViewLayoutEditable
} from 'cadenza/workbook/workbook.state';
import { getStore } from 'cadenza/store/cadenza-store';
import { isOperationModeSimplified } from 'cadenza/workbook/operation-mode';

/**
 * Consists of [ matchingFunction, newValue ].
 * If the matchingFunction returns true, the newValue will be taken.
 */
type FeatureOverrideRule = [(state: WorkbookState) => boolean, boolean];

/**
 * A constant to rule them all. It assigns a list of FeatureOverrideRules to a WorkbookUiFeatureName.
 * If the matchingFunction returns true, the boolean next to it will be taken as overwritten value.
 * The last matching rule wins, so they are in reverse order of priority. If no matches or no rules
 * are defined for a feature the normal value of isSingleFeatureAvailable() will be taken.
 *
 * See also FEATURE_DEPENDENCIES constant.
 *
 * Example:
 * {'workbook-condition-management': [ [ isOperationModeSimplified, false ], ... ], ...}
 * => If the matchingFunction isOperationModeSimplified returns true the newValue false will be taken.
 */
const WORKBOOK_UI_FEATURE_OVERRIDE_RULES: Partial<Record<WorkbookUiFeatureName, FeatureOverrideRule[]>> = {
  'workbook-condition-management': [
    [ isOperationModeSimplified, false ]
  ],
  'workbook-data-browser': [
    [ isOperationModeSimplified, false ]
  ],
  'workbook-view-management': [
    [ isOperationModeSimplified, false ]
  ],
  'workbook-workbook-management': [
    [ isOperationModeSimplified, false ]
  ],
  'workbook-design': [
    [ (state) => { return isOperationModeSimplified(state) && !isWorkbookDesignerEnabled(state); }, false ]
  ],
  'workbook-data-view-content': [
    [ (state) => { return isOperationModeSimplified(state) && !isWorkbookDataViewContentEditable(state); }, false ]
  ],
  'workbook-layout-management': [
    [ (state) => { return isOperationModeSimplified(state) && !isWorkbookViewLayoutEditable(state); }, false ]
  ]
};

export function isWorkbookFeatureAvailable (feature: AllFeatures, state: WorkbookState = getStore().getState()): boolean {
  return isSingleWorkbookFeatureAvailable(feature, state)
      && getDependentFeatures(feature).every(f => isWorkbookFeatureAvailable(f, state));
}

function isSingleWorkbookFeatureAvailable (feature: AllFeatures, state: WorkbookState) {
  const initialAvailability = isSingleFeatureAvailable(feature);
  return feature in WORKBOOK_UI_FEATURE_OVERRIDE_RULES && isWorkbookFeature(feature)
    ? applyFeatureOverrideRules(WORKBOOK_UI_FEATURE_OVERRIDE_RULES[feature]!, state, initialAvailability)
    : initialAvailability;
}

/**
 * A function that checks all FeatureOverrideRules for a feature and overrides the initialAvailability if a rule fits.
 * Because of the reduce the last matching rule wins, so rules are sorted in reverse order of priority.
 */
function applyFeatureOverrideRules (overrideRules: FeatureOverrideRule[], state: WorkbookState, initialAvailability = true) {
  return overrideRules.reduce((accumulator, currentRule) => {
    const [ matchingFunction, newValue ] = currentRule;
    return matchingFunction(state)
      ? newValue
      : accumulator;
  }, initialAvailability);
}
